import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from './components/ErrorBoundary';
import AppQueryProvider from './components/AppQueryProvider';
import BrandedLoadingScreen from './components/BrandedLoadingScreen';
import SaasMarketingBootShell, { isSaasMarketingBootPath } from './saas/components/SaasMarketingBootShell';
import { initializeSecurityProtection } from './utils/securityProtection';
import { getAppMode } from './saas/appMode';
import {
  isSaasPlatformHost,
  isToolsAndBubblesHost,
  SERVICEAPP_BRAND,
  applyTbBootDocument,
} from './share/serviceappBrand';
import { recoverSaasOAuthReturnIfNeeded, isOAuthCallbackUrl } from './saas/lib/saasOAuthReturn';
import { hasLikelyStoredSession, hasOAuthCallbackParams } from './utils/publicRouteEarlyRender';
import { syncAppBuildVersion } from './utils/appBuildSync';
import { initSentry, isSentryEnabled } from './utils/sentry';
import { hydrateAuthStorageFromNative } from './lib/authStorage';
import { isChunkLoadError } from './utils/chunkLoadError';
import { scheduleTbStaticShellDismiss } from './utils/dismissTbStaticShell';
import './index.css';

/**
 * Dual-deployment mode: 'tb-site' (toolsandbubbles.com, unchanged) or
 * 'saas-platform' (serviceapp.ai). The T&B code path is identical to before.
 */
const oauthReturnRecovering = typeof window !== 'undefined' && recoverSaasOAuthReturnIfNeeded();

if (typeof window !== 'undefined' && window.location.hostname.toLowerCase() === 'toolsandbubbles.com') {
  window.location.replace(
    `https://www.toolsandbubbles.com${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
}

if (typeof window !== 'undefined' && !oauthReturnRecovering && !isOAuthCallbackUrl()) {
  syncAppBuildVersion();
}

const appMode = getAppMode();

const isTbHost = typeof window !== 'undefined' && isToolsAndBubblesHost(window.location.hostname);
const isSaasHost = typeof window !== 'undefined' && isSaasPlatformHost(window.location.hostname);
/** T&B hostname always wins over a mis-set VITE_APP_MODE=saas-platform build env. */
const useSaasApp = isSaasHost || (!isTbHost && appMode === 'saas-platform');

// On the SaaS domain or SaaS dev/build, drop T&B static LCP shell before paint.
function applySaasBootDocument() {
  document.getElementById('tb-static-shell')?.remove();
  document.documentElement.classList.add('saas-platform');
  document.title = SERVICEAPP_BRAND.title;
  document
    .querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
    .forEach((el) => el.remove());
  const addIcon = (rel: string, href: string, sizes?: string) => {
    const link = document.createElement('link');
    link.rel = rel;
    link.type = 'image/png';
    link.href = href;
    if (sizes) link.setAttribute('sizes', sizes);
    document.head.appendChild(link);
  };
  addIcon('icon', SERVICEAPP_BRAND.icon);
  addIcon('icon', SERVICEAPP_BRAND.icon192, '192x192');
  addIcon('icon', SERVICEAPP_BRAND.icon512, '512x512');
  addIcon('shortcut icon', SERVICEAPP_BRAND.icon);
  addIcon('apple-touch-icon', SERVICEAPP_BRAND.icon512, '512x512');
  const manifest = document.querySelector('link[rel="manifest"]');
  if (manifest) {
    manifest.setAttribute('href', SERVICEAPP_BRAND.manifest);
  } else {
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = SERVICEAPP_BRAND.manifest;
    document.head.appendChild(link);
  }
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', SERVICEAPP_BRAND.themeColor);
  document.querySelector('meta[name="application-name"]')?.setAttribute('content', 'ServiceApp');
  document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute('content', 'ServiceApp');
}

if (typeof window !== 'undefined' && isTbHost) {
  applyTbBootDocument();
} else if (typeof window !== 'undefined' && useSaasApp) {
  applySaasBootDocument();
}

if (typeof window !== 'undefined' && !useSaasApp) {
  document.documentElement.classList.remove('dark');
  document.documentElement.style.colorScheme = 'light';
  document.documentElement.style.setProperty('color-scheme', 'light only');
}

const TbApp = lazy(() => import('./App.tsx'));
const SaasApp = lazy(() => import('./saas/SaasApp.tsx'));
const App = useSaasApp ? SaasApp : TbApp;

// Initialize security protection (only in production)
// Allow devtools for admin users
if (appMode === 'tb-site') {
  initializeSecurityProtection().catch((error) => {
    console.error('Failed to initialize security protection:', error);
  });
}

// Load WhatsApp template testing utilities (dev only)
if (import.meta.env.DEV) {
  import('./utils/testWhatsAppTemplates');
}

// Global error handler for chunk loading failures
// Add reload protection to prevent infinite loops
let reloadAttempts = 0;
const MAX_RELOAD_ATTEMPTS = 2;
const RELOAD_COOLDOWN_KEY = 'last_reload_timestamp';
const COOLDOWN_MS = 5000; // 5 seconds

// Also catch unhandled promise rejections (dynamic import failures surface here)
window.addEventListener('unhandledrejection', (event) => {
  const errorMessage = event.reason?.message || String(event.reason) || '';
  if (isChunkLoadError(event.reason) || isChunkLoadError(errorMessage)) {
    console.error('Dynamic import rejection detected:', errorMessage);
    // In development, do not auto-reload to avoid "page keeps refreshing every few seconds"
    if (import.meta.env.DEV) {
      console.warn('[Dev] Chunk load error – no auto-reload. Fix the cause or refresh manually.');
      return;
    }
    const lastReload = localStorage.getItem(RELOAD_COOLDOWN_KEY);
    const now = Date.now();
    if (!lastReload || now - parseInt(lastReload) >= COOLDOWN_MS) {
      localStorage.setItem(RELOAD_COOLDOWN_KEY, now.toString());
      event.preventDefault();
      window.location.reload();
    }
  }
});

window.addEventListener('error', (event) => {
  const error = event.error || event.message;
  const errorMessage = typeof error === 'string' ? error : error?.message || '';

  if (isChunkLoadError(error) || isChunkLoadError(errorMessage)) {
    console.error('Chunk loading error detected:', errorMessage);
    // In development, do not auto-reload to avoid "page keeps refreshing every few seconds"
    if (import.meta.env.DEV) {
      console.warn('[Dev] Chunk load error – no auto-reload. Fix the cause or refresh manually.');
      event.preventDefault();
      return;
    }
    // Check if we're in a reload loop
    const lastReload = localStorage.getItem(RELOAD_COOLDOWN_KEY);
    const now = Date.now();

    if (lastReload && now - parseInt(lastReload) < COOLDOWN_MS) {
      reloadAttempts++;
      console.warn(`Reload attempt ${reloadAttempts}/${MAX_RELOAD_ATTEMPTS}`);

      if (reloadAttempts >= MAX_RELOAD_ATTEMPTS) {
        console.error('Too many reload attempts, stopping to prevent infinite loop');
        localStorage.removeItem(RELOAD_COOLDOWN_KEY);
        event.preventDefault();
        return;
      }
    } else {
      reloadAttempts = 1;
    }

    localStorage.setItem(RELOAD_COOLDOWN_KEY, now.toString());

    // Unregister service worker and reload
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        Promise.all(registrations.map((r) => r.unregister())).then(() => {
          console.log('Service workers unregistered, reloading...');
          setTimeout(() => window.location.reload(), 500);
        });
      });
    } else {
      setTimeout(() => window.location.reload(), 500);
    }

    event.preventDefault();
  }
});

const root = createRoot(document.getElementById('root')!);

/**
 * StrictMode is on for the SaaS surface in development.
 *
 * It was switched off across the whole app while a hooks-order crash (React #310) was being
 * chased, and then stayed off, which meant the double-render checks never came back for the
 * newer code either. Turning it on everywhere at once would reintroduce a known crash in the
 * legacy T&B tree, so it comes back where the code is new enough to survive it.
 *
 * Development only, because that is where the extra render and the warnings happen; a
 * production bundle behaves identically either way. Set VITE_REACT_STRICT_MODE=false to
 * silence it locally — but a crash under StrictMode is a real bug in the component, not a
 * quirk of the flag.
 */
const strictModeEnabled =
  import.meta.env.DEV && useSaasApp && import.meta.env.VITE_REACT_STRICT_MODE !== 'false';

function clearStaticBootShells() {
  document.getElementById('tb-static-shell')?.remove();
}

const saasFallback =
  typeof window !== 'undefined' && isSaasMarketingBootPath(window.location.pathname) ? (
    <SaasMarketingBootShell />
  ) : (
    <BrandedLoadingScreen />
  );

async function mountApp() {
  // Restore auth from native Preferences before Supabase reads storage (keeps mobile users signed in).
  if (typeof window !== 'undefined') {
    try {
      await hydrateAuthStorageFromNative(import.meta.env.VITE_SUPABASE_URL);
    } catch {
      /* non-critical */
    }
  }

  if (useSaasApp && isSentryEnabled()) {
    initSentry();
  }

  const tbAuthFallback =
    !useSaasApp && (hasLikelyStoredSession() || hasOAuthCallbackParams() || oauthReturnRecovering);

  const appTree = oauthReturnRecovering ? (
    saasFallback
  ) : (
    <Suspense fallback={useSaasApp || tbAuthFallback ? saasFallback : null}>
      <App />
    </Suspense>
  );

  const guarded = (
    <HelmetProvider>
      <ErrorBoundary>
        <AppQueryProvider>{appTree}</AppQueryProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
  root.render(strictModeEnabled ? <StrictMode>{guarded}</StrictMode> : guarded);

  if (useSaasApp || isSaasHost) {
    requestAnimationFrame(() => clearStaticBootShells());
  }
}

void mountApp();
if (typeof window !== 'undefined' && !useSaasApp) {
  scheduleTbStaticShellDismiss();
}

const registerServiceWorker = () => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const register = () => {
    navigator.serviceWorker
      .register('/sw-pwa.js')
      .then((registration) => {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                newWorker.postMessage({ type: 'SKIP_WAITING' });
              }
            });
          }
        });
      })
      .catch((error) => {
        console.error('Service worker registration failed:', error);
      });
  };

  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener('load', register, { once: true });
  }

  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'FORCE_RELOAD') {
      window.location.reload();
    }
  });
};

/** Minimal SW for PWA + Web Push; does not intercept fetch (avoids stale bundle / CORS issues). */
const shouldRegisterPwa = import.meta.env.PROD || Boolean(import.meta.env.VITE_VAPID_PUBLIC_KEY);
const isKochiBookingHost =
  typeof window !== 'undefined' &&
  ['cleaningserviceinkochi.com', 'www.cleaningserviceinkochi.com'].includes(
    window.location.hostname.toLowerCase(),
  );
if (shouldRegisterPwa && !isKochiBookingHost && (appMode === 'tb-site' || useSaasApp || isSaasHost)) {
  registerServiceWorker();
}
